import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { PasswordInput } from '../components/ui/password-input';
import { Loader2, CheckCircle2 } from 'lucide-react';
import MFAVerify from '../components/MFAVerify';
import MFASetupRequired from '../components/MFASetupRequired';
import { authAPI, billingAPI } from '../lib/api';
import { LogoPublic } from '../components/Logo';

export const Login = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const justVerified = searchParams.get('verified') === '1';
  const [identifier, setIdentifier] = useState(''); // email OR username
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // MFA state
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaSetupRequired, setMfaSetupRequired] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [userName, setUserName] = useState('');
  
  const { login, setAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.from?.pathname;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authAPI.login({ identifier, password });
      const data = response.data;
      
      // Check for MFA requirements
      if (data.mfa_required) {
        setMfaRequired(true);
        setMfaToken(data.mfa_token);
        setUserName(data.user?.name || '');
        return;
      }
      
      if (data.mfa_setup_required) {
        setMfaSetupRequired(true);
        setMfaToken(data.temp_token);
        // Store temp credentials for setup
        localStorage.setItem('temp_auth', JSON.stringify({ identifier, password }));
        return;
      }
      
      // Normal login - complete auth
      await completeLogin(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const completeLogin = async (data) => {
    // Use the auth context to store tokens and user
    if (data.access_token) {
      await setAuth({
        token: data.access_token,
        refreshToken: data.refresh_token,
        user: data.user
      });
      
      // Check if legal acceptance is required
      if (data.legal_required) {
        navigate('/legal/accept', { replace: true });
        return;
      }
      
      // Evaluate subscription/billing status before routing
      try {
        const billingRes = await billingAPI.getBetaTrialStatus();
        if (billingRes.data.billing_required) {
          navigate('/billing', { replace: true });
          return;
        }
      } catch {
        // If billing check fails, proceed to default route
      }
      
      // Navigate to the page they were trying to reach, or default
      if (returnTo && returnTo !== '/login' && returnTo !== '/') {
        navigate(returnTo, { replace: true });
      } else if (data.user?.role === 'tech') {
        navigate('/tech', { replace: true });
      } else if (data.user?.assigned_van_id) {
        navigate(`/vans/${data.user.assigned_van_id}`, { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  };

  const handleMFASuccess = async (data) => {
    await completeLogin(data);
  };

  const handleMFACancel = () => {
    setMfaRequired(false);
    setMfaSetupRequired(false);
    setMfaToken('');
    localStorage.removeItem('temp_auth');
  };

  const handleSetupSuccess = async (data) => {
    localStorage.removeItem('temp_auth');
    if (data) {
      await completeLogin(data);
    } else {
      // Re-login with credentials after setup
      try {
        const tempAuth = localStorage.getItem('temp_auth');
        if (tempAuth) {
          const { identifier: savedIdentifier, password: savedPassword } = JSON.parse(tempAuth);
          const response = await authAPI.login({ identifier: savedIdentifier, password: savedPassword });
          if (response.data.mfa_required) {
            setMfaRequired(true);
            setMfaToken(response.data.mfa_token);
            setMfaSetupRequired(false);
          }
        }
      } catch {
        // Redirect to login
        setMfaSetupRequired(false);
      }
    }
  };

  // Show MFA verification screen
  if (mfaRequired) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center mb-8">
            <LogoPublic size="md" className="mb-2" />
          </div>
          <MFAVerify
            mfaToken={mfaToken}
            userName={userName}
            onSuccess={handleMFASuccess}
            onCancel={handleMFACancel}
          />
        </div>
      </div>
    );
  }

  // Show MFA setup required screen
  if (mfaSetupRequired) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center mb-8">
            <LogoPublic size="md" className="mb-2" />
          </div>
          <MFASetupRequired
            tempToken={mfaToken}
            onSuccess={handleSetupSuccess}
            onCancel={handleMFACancel}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex flex-col items-center mb-4">
          <LogoPublic size="lg" />
          <p className="text-muted-foreground mt-1">{t('auth.fleetTagline')}</p>
        </div>

        <Card className="border-border shadow-sm">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold font-heading">{t('auth.signIn')}</CardTitle>
            <CardDescription>{t('auth.enterCredentials')}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {justVerified && (
                <div className="flex items-center gap-2 p-3 text-sm text-green-700 bg-green-50 rounded-md" data-testid="verified-banner">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                  Email verified. Please log in.
                </div>
              )}
              {error && (
                <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md" data-testid="login-error">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="identifier" className="text-sm font-bold uppercase tracking-wide">
                  {t('auth.emailOrUsername')}
                </Label>
                <Input
                  id="identifier"
                  type="text"
                  placeholder={t('auth.emailOrUsernamePlaceholder')}
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  required
                  className="h-12 text-lg"
                  data-testid="login-identifier"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-bold uppercase tracking-wide">
                  {t('auth.password')}
                </Label>
                <PasswordInput
                  id="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 text-lg"
                  data-testid="login-password"
                />
              </div>

              <div className="flex items-center justify-end">
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary hover:underline"
                  data-testid="forgot-password-link"
                >
                  {t('auth.forgotPassword')}
                </Link>
              </div>

              <Button
                type="submit"
                className="w-full h-12 text-base font-bold btn-press"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('auth.signingIn')}
                  </>
                ) : (
                  t('auth.signIn')
                )}
              </Button>
            </form>

            <p className="mt-6 text-center text-sm text-muted-foreground">
              {t('auth.noAccount')}{' '}
              <Link to="/signup" className="text-primary font-semibold hover:underline" data-testid="signup-link">
                {t('auth.signUp')}
              </Link>
            </p>

            <div className="mt-4 flex items-center justify-center gap-3 text-xs text-muted-foreground">
              <a
                href="https://stockandload.com/terms-of-service/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
                data-testid="login-terms-link"
              >
                Terms of Service
              </a>
              <span>&middot;</span>
              <a
                href="https://stockandload.com/privacy-policy/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
                data-testid="login-privacy-link"
              >
                Privacy Policy
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
